<script setup>
const props = defineProps({
    reason: { type: String, default: null },
    view: { type: String, default: 'list' },
})
const emit = defineEmits(['build', 'findSession', 'clearFilters', 'viewAll', 'createEvent'])

// Overview isn't day-scoped, so a time-range-empty result has no single slot to
// fill — collapse it into the "nothing matches your filters" case there.
const effectiveReason = computed(() =>
    props.view === 'overview' && props.reason === 'none_in_time_range' ? 'no_filter_match' : props.reason
)

// The schedule-assistant "sparkle" glyph — the shared marker for Build /
// Find a Session actions (matches the Build button treatment in agenda.vue).
const SPARKLE_PATHS = [
    'M13 8.09719C13 8.21502 12.9044 8.31057 12.7875 8.31057C10.9214 8.31057 8.71296 10.5186 8.71296 12.3843C8.71296 12.5021 8.61738 12.5977 8.50046 12.5977C8.38354 12.5977 8.28797 12.5021 8.28797 12.3843C8.28704 10.5186 6.07856 8.31057 4.2125 8.31057C4.09465 8.31057 4 8.21502 4 8.09719C4 7.97937 4.09558 7.88381 4.2125 7.88381C6.07856 7.88381 8.28704 5.67579 8.28704 3.81104C8.28704 3.69321 8.38262 3.59766 8.49954 3.59766C8.61645 3.59766 8.71203 3.69321 8.71203 3.81104C8.71203 5.67579 10.9205 7.88381 12.7866 7.88381C12.9044 7.88381 12.9991 7.97937 12.9991 8.09719H13Z',
    'M6 3.59735C6 3.6759 5.93628 3.7396 5.85834 3.7396C4.61429 3.7396 3.14197 5.21161 3.14197 6.4554C3.14197 6.53395 3.07826 6.59766 3.00031 6.59766C2.92236 6.59766 2.85865 6.53395 2.85865 6.4554C2.85803 5.21161 1.38571 3.7396 0.141664 3.7396C0.0630993 3.7396 0 3.6759 0 3.59735C0 3.5188 0.0637179 3.45509 0.141664 3.45509C1.38571 3.45509 2.85803 1.98308 2.85803 0.73991C2.85803 0.661361 2.92174 0.597656 2.99969 0.597656C3.07764 0.597656 3.14135 0.661361 3.14135 0.73991C3.14135 1.98308 4.61367 3.45509 5.85772 3.45509C5.93628 3.45509 5.99938 3.5188 5.99938 3.59735H6Z',
]
</script>

<template>
    <div class="empty flex flex-col items-center justify-center py-[60px] lg:py-[90px]">
        <!-- Case 2: nothing scheduled in the selected time range -->
        <template v-if="effectiveReason === 'none_in_time_range'">
            <h2>Nothing on your schedule during this time.</h2>
            <p>Find a session to fill the slot, or widen your time range to see the rest of your day.</p>
            <div class="actions">
                <button type="button" data-test="empty-find" class="ms-cta-filled ms-cta-build" @click="emit('findSession')">
                    <svg viewBox="0 0 13 13" fill="none" class="size-[13px] shrink-0" aria-hidden="true">
                        <path v-for="d in SPARKLE_PATHS" :key="d" :d="d" fill="currentColor" />
                    </svg>
                    Find a Session
                </button>
                <button type="button" data-test="empty-clear" class="ms-cta-outline" @click="emit('clearFilters')">View Full Schedule</button>
            </div>
        </template>

        <!-- Case 4: filtered to My Event, and there are none in scope. Distinct
             from Case 3 because the viewer wasn't looking for sessions — telling
             them their sessions don't match, and offering View Full Schedule, is
             both untrue and useless. The action they want is to create one. -->
        <template v-else-if="effectiveReason === 'no_my_events'">
            <!-- Overview shows every day at once, so "for this day" would be a lie there. -->
            <h2 v-if="view === 'overview'">You haven't created any events yet.</h2>
            <h2 v-else>You haven't created any events for this day.</h2>
            <p>Add your own events such as meetings, lunch, or time on the show floor to check out the experiences, activations and sponsors.</p>
            <div class="actions">
                <button type="button" data-test="empty-create-event" class="ms-cta-filled" @click="emit('createEvent')">Create My Event</button>
                <button type="button" data-test="empty-clear" class="ms-cta-outline" @click="emit('clearFilters')">View Full Schedule</button>
            </div>
        </template>

        <!-- Case 3: content filters exclude everything -->
        <template v-else-if="effectiveReason === 'no_filter_match'">
            <h2>None of your scheduled sessions match these filters.</h2>
            <p>Clear your filters to see everything on your schedule.</p>
            <div class="actions">
                <button type="button" data-test="empty-clear" class="ms-cta-outline" @click="emit('clearFilters')">Clear All Filters</button>
            </div>
        </template>

        <!-- Case 1: nothing scheduled on the day (default) -->
        <template v-else>
            <h2>You haven't added anything to My Schedule for this day.</h2>
            <p>Add a session for this date, create your own event, or use the schedule builder to build a day just for you.</p>
            <!-- actions-stack-sm: this pair is too long to share a phone row —
                 each label wrapped to two lines inside its pill (Nick, UNB-745). -->
            <div class="actions actions-stack-sm">
                <button type="button" data-test="empty-build" class="ms-cta-filled ms-cta-build" @click="emit('build')">
                    <svg viewBox="0 0 13 13" fill="none" class="size-[13px] shrink-0" aria-hidden="true">
                        <path v-for="d in SPARKLE_PATHS" :key="d" :d="d" fill="currentColor" />
                    </svg>
                    Build My Schedule For This Day
                </button>
                <button type="button" data-test="empty-viewall" class="ms-cta-outline" @click="emit('viewAll')">View All Sessions</button>
            </div>
        </template>
    </div>
</template>
